import React from 'react';
import type { SiteRagReport } from '../../../../types/siteRag';
import { walkCitationText } from '../../../../lib/walkCitationText';
import { useEffectiveReportTheme } from '../../../../lib/useEffectiveReportTheme';
import { roleColor } from '../../../../lib/reportRoleColors';
import { EditorialBadge, type EditorialBadgeTone } from '../../report/editorial';
import { SectionShell } from '../SectionShell';
import { scmRiskItems } from '../../../../lib/reportRendering/supplyChainPlan';

/** Risk severity → EditorialBadge tone (replaces the bare muted text). */
function severityTone(severity: string | undefined): EditorialBadgeTone {
  switch (severity) {
    case 'critical':
    case 'high':
      return 'negative';
    case 'medium':
      return 'warning';
    case 'low':
      return 'positive';
    default:
      return 'neutral';
  }
}

export const SupplyChainRisksSection: React.FC<{ report: SiteRagReport; label: string }> = ({ report, label }) => {
  const theme = useEffectiveReportTheme();
  const filtered = scmRiskItems(report);
  const risks = filtered.length > 0 ? filtered : (report.risks ?? []);
  const intro = report.supplyChainAnalysis?.supplyChainRiskSynthesis?.trim();
  const hasData = risks.length > 0 || !!intro;

  return (
    <SectionShell label={label} hasData={hasData} report={report} sectionId="supplyChainRisks" required>
      {intro && (
        <p className="text-sm mb-4" style={{ color: roleColor(theme, 'body') }}>{walkCitationText(intro, report.references)}</p>
      )}
      <ul className="space-y-3">
        {risks.map((risk, idx) => (
          <li
            key={risk.id ?? `${risk.label ?? 'risk'}-${idx}`}
            className="p-3 text-sm space-y-1"
            style={{ borderTop: `1px solid ${roleColor(theme, 'hair')}` }}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="font-bold" style={{ color: roleColor(theme, 'ink') }}>{risk.label}</span>
              {risk.severity && (
                <EditorialBadge theme={theme} tone={severityTone(risk.severity)} variant="outline">
                  {risk.severity}
                </EditorialBadge>
              )}
            </div>
            {risk.impact && (
              <p style={{ color: roleColor(theme, 'body') }}>{walkCitationText(risk.impact, report.references)}</p>
            )}
            {risk.mitigation && (
              <p className="text-xs italic" style={{ color: roleColor(theme, 'muted') }}>
                Mitigation: {walkCitationText(risk.mitigation, report.references)}
              </p>
            )}
          </li>
        ))}
      </ul>
    </SectionShell>
  );
};
